import { useState, useCallback, useEffect } from "react";

/**
 * Market Domination Report — a shareable one-pager built from a prospect URL.
 * Premium dark editorial design. Includes share-link view analytics so reps
 * know exactly when a client opened their report.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const todayLabel = () =>
  new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const fmtAgo = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return fmtWhen(iso);
};

const fmtDur = (sec) => {
  const n = Number(sec) || 0;
  if (n < 60) return `${n}s`;
  return `${Math.floor(n / 60)}m ${n % 60}s`;
};

const parseCount = (v) => {
  if (v == null) return 0;
  const m = String(v).replace(/[,~+]/g, "").match(/\d+/);
  return m ? parseInt(m[0], 10) : 0;
};

const MarketReport = () => {
  const [url, setUrl] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advBiz, setAdvBiz] = useState("");
  const [advLocation, setAdvLocation] = useState("");
  const [advTrade, setAdvTrade] = useState("");

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState(null);
  const [record, setRecord] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/sales/market-report?limit=10`, {
        credentials: "include",
      });
      if (r.ok) setHistory(await r.json());
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleDelete = useCallback(async (h, e) => {
    e.stopPropagation();
    e.preventDefault();
    const label = h.business_name || h.domain || "this report";
    if (!window.confirm(`Delete the report for ${label}? Share links stop working right away.`)) return;
    try {
      const resp = await fetch(
        `${API}/api/sales/market-report/${encodeURIComponent(h.created_at)}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!resp.ok) {
        setStatus("Couldn't delete that one — try again.");
        return;
      }
      // If the deleted record is currently open in the report pane, clear it
      if (record && record.created_at === h.created_at) {
        setReport(null);
        setRecord(null);
        setStatus("Deleted.");
      }
      loadHistory();
    } catch (err) {
      setStatus("Couldn't delete that one — try again.");
    }
  }, [record, loadHistory]);

  const handleGenerate = useCallback(async () => {
    if (!url.trim()) {
      setStatus("Enter a website URL to start.");
      return;
    }
    setLoading(true);
    setStatus("Reading their site…");
    setReport(null);
    try {
      const resp = await fetch(`${API}/api/sales/market-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          url: url.trim(),
          business: advBiz.trim() || undefined,
          city: advLocation.trim() || undefined,
          trade: advTrade.trim() || undefined,
          force: true,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setStatus(data.detail || "Something went wrong. Try again.");
        setLoading(false);
        return;
      }

      if (data.status === "done" && data.record) {
        setRecord(data.record);
        setReport(data.record.report);
        setStatus("Loaded cached report.");
        setLoading(false);
        loadHistory();
        return;
      }

      const jobId = data.job_id;
      if (!jobId) {
        setStatus("No job id returned. Try again.");
        setLoading(false);
        return;
      }
      setStatus("Researching the market…");
      const started = Date.now();
      const MAX_MS = 180000;
      let step = 0;
      while (Date.now() - started < MAX_MS) {
        const wait = Math.min(2000 + step * 1000, 5000);
        await new Promise((r) => setTimeout(r, wait));
        step += 1;
        if (step === 4) setStatus("Writing the report…");
        try {
          const jr = await fetch(`${API}/api/sales/market-report/job/${jobId}`, {
            credentials: "include",
          });
          if (!jr.ok) continue;
          const j = await jr.json();
          if (j.status === "done" && j.record) {
            setRecord(j.record);
            setReport(j.record.report);
            setStatus("Report ready.");
            setLoading(false);
            loadHistory();
            return;
          }
          if (j.status === "failed") {
            const errMsg = j.error || "Report failed. Try again.";
            setStatus(errMsg);
            // Auto-open the Advanced pane when auto-detect fails so the rep
            // sees exactly what to fill in.
            if (/auto-detect|advanced fields/i.test(errMsg)) {
              setShowAdvanced(true);
            }
            setLoading(false);
            return;
          }
        } catch (e) { /* keep polling */ }
      }
      setStatus("Report is taking longer than expected — check Recent in a minute.");
      setLoading(false);
    } catch (e) {
      setStatus(`Error: ${e.message || "network"}. Try again.`);
      setLoading(false);
    }
  }, [url, advBiz, advLocation, advTrade, loadHistory]);

  const handleCopy = () => {
    if (!report) return;
    const flat = plainTextFromReport(report);
    navigator.clipboard.writeText(flat).then(() => setStatus("Report copied to clipboard."));
  };

  const handleCopyShareLink = async () => {
    if (!record) return;
    let shareId = record.share_id;
    if (!shareId) {
      try {
        const r = await fetch(`${API}/api/sales/market-report/${encodeURIComponent(record.created_at)}/share`, {
          method: "POST",
          credentials: "include",
        });
        const d = await r.json();
        if (r.ok && d.share_id) {
          shareId = d.share_id;
          setRecord({ ...record, share_id: shareId });
        } else {
          setStatus(d.detail || "Couldn't mint a share link.");
          return;
        }
      } catch (e) {
        setStatus(`Share link error: ${e.message || "network"}`);
        return;
      }
    }
    const shareUrl = `${window.location.origin}/report/${shareId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setStatus(`Share link copied — ${shareUrl}`);
    } catch {
      setStatus(shareUrl);
    }
  };

  return (
    <div className="mr-shell" data-testid="market-report-tab">
      <style>{styles}</style>

      {/* ─── Input Pane ─── */}
      <section className="mr-input" data-testid="mr-input-pane">
        <div className="mr-input-inner">
          <div className="mr-input-title">Market Domination Report</div>
          <div className="mr-input-sub">Paste the new client&apos;s website. We&apos;ll build the pre-kickoff briefing you send them before their kickoff call.</div>

          <div className="mr-input-row">
            <input
              type="url"
              className="mr-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://theirbusiness.com"
              onKeyDown={(e) => { if (e.key === "Enter" && !loading) handleGenerate(); }}
              data-testid="mr-url-input"
            />
            <button
              type="button"
              className="mr-generate"
              onClick={handleGenerate}
              disabled={loading}
              data-testid="mr-generate-btn"
            >
              {loading ? "Working…" : "Generate Report"}
            </button>
          </div>

          <button
            type="button"
            className="mr-advanced-toggle"
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="mr-advanced-toggle"
          >
            {showAdvanced ? "− Hide advanced" : "+ Advanced (only if auto-detect misses)"}
          </button>

          {showAdvanced && (
            <div className="mr-adv-row" data-testid="mr-adv-row">
              <input className="mr-adv-input" placeholder="Business name" value={advBiz} onChange={(e) => setAdvBiz(e.target.value)} data-testid="mr-adv-biz" />
              <input className="mr-adv-input" placeholder="City, ST (e.g. Fort Myers, FL)" value={advLocation} onChange={(e) => setAdvLocation(e.target.value)} data-testid="mr-adv-location" />
              <input className="mr-adv-input" placeholder="Trade (e.g. Roofing)" value={advTrade} onChange={(e) => setAdvTrade(e.target.value)} data-testid="mr-adv-trade" />
            </div>
          )}

          <div
            className={`mr-status ${loading ? "loading" : ""} ${!loading && /error|failed|couldn'?t|try again|please fill|blocked/i.test(status || "") ? "err" : ""}`}
            data-testid="mr-status"
          >
            {loading && <span className="mr-spinner" />}
            <span>{status || "Ready."}</span>
          </div>

          {history.length > 0 && (
            <div className="mr-history">
              <div className="mr-history-label">Recent</div>
              <div className="mr-history-list">
                {history.slice(0, 6).map((h, i) => (
                  <div key={i} className="mr-history-chip-wrap">
                    <button
                      className="mr-history-chip"
                      onClick={() => {
                        setUrl(h.url);
                        setReport(h.report);
                        setRecord(h);
                        setStatus(`Loaded cached report for ${h.business_name}.`);
                      }}
                      title={h.url}
                      data-testid={`mr-history-chip-${i}`}
                    >
                      <span>{h.business_name || h.domain}</span>
                      {h.view_stats?.views > 0 && (
                        <span className="mr-chip-views" data-testid={`mr-history-views-${i}`}>
                          <EyeIcon /> {h.view_stats.views}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="mr-history-del"
                      onClick={(e) => handleDelete(h, e)}
                      title="Delete this report"
                      aria-label={`Delete report for ${h.business_name || h.domain}`}
                      data-testid={`mr-history-del-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── Report Pane ─── */}
      <section className="mr-report-pane" data-testid="mr-report-pane">
        {!report && !loading && (
          <div className="mr-empty">
            <div className="mr-empty-icon">◆</div>
            <div className="mr-empty-title">Drop the client&apos;s website above to build the briefing.</div>
            <div className="mr-empty-sub">Runs through Jina Reader, cross-checks Google, and hands you a live link to send before the kickoff call.</div>
          </div>
        )}

        {loading && !report && (
          <div className="mr-empty">
            <div className="mr-spinner big" />
            <div className="mr-empty-title">{status}</div>
            <div className="mr-empty-sub">~15-25 seconds. Don&apos;t close the tab.</div>
          </div>
        )}

        {report && (
          <div className="mr-report-col">
            <ViewAnalytics shareId={record?.share_id} />
            <ReportOnePager
              report={report}
              record={record}
              onCopy={handleCopy}
              onCopyShareLink={handleCopyShareLink}
            />
          </div>
        )}
      </section>
    </div>
  );
};

// ─── View Analytics (private tab only) ──────────────────────────────────────

const EyeIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block", verticalAlign: "-1px" }}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const ViewAnalytics = ({ shareId }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setData(null);
    setExpanded(false);
    if (!shareId) return;
    let dead = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/sales/market-report/views/${shareId}`, { credentials: "include" });
        if (r.ok && !dead) setData(await r.json());
      } catch { /* ignore */ }
    })();
    return () => { dead = true; };
  }, [shareId]);

  if (!shareId || !data) return null;

  const views = data.views || [];
  const count = data.count || 0;

  if (count === 0) {
    return (
      <div className="va-strip empty" data-testid="mr-view-analytics">
        <span className="va-eye"><EyeIcon /></span>
        <span className="va-none">No opens yet — send the share link and you&apos;ll see exactly when they read it.</span>
      </div>
    );
  }

  const lastOpen = views[0]?.opened_at;
  const maxSec = Math.max(...views.map((v) => Number(v.seconds) || 0));
  const maxScroll = Math.max(...views.map((v) => Number(v.max_scroll_pct) || 0));

  return (
    <div className="va-strip" data-testid="mr-view-analytics">
      <div className="va-row">
        <span className="va-eye live"><EyeIcon /></span>
        <span className="va-stat"><b>{count}</b> open{count === 1 ? "" : "s"}</span>
        <span className="va-dot">·</span>
        <span className="va-stat">last opened <b>{fmtAgo(lastOpen)}</b></span>
        <span className="va-dot">·</span>
        <span className="va-stat">longest read <b>{fmtDur(maxSec)}</b></span>
        <span className="va-dot">·</span>
        <span className="va-stat">read depth <b>{maxScroll}%</b></span>
        <button className="va-toggle" onClick={() => setExpanded((v) => !v)} data-testid="mr-view-toggle">
          {expanded ? "Hide opens" : "All opens"}
        </button>
      </div>
      {expanded && (
        <div className="va-sessions" data-testid="mr-view-sessions">
          {views.map((v, i) => (
            <div key={i} className="va-session">
              <span className="va-s-when">{fmtWhen(v.opened_at)}</span>
              <span className="va-s-detail">read {fmtDur(v.seconds)} · scrolled {v.max_scroll_pct || 0}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── The Report One-Pager (shared with public page) ─────────────────────────

const SectionHead = ({ num, title, sub }) => (
  <div className="sec-head">
    <div className="sec-topline">
      <span className="sec-num">{num}</span>
      <span className="sec-rule" />
    </div>
    <h2 className="sec-title">{title}</h2>
    {sub && <div className="sec-sub">{sub}</div>}
  </div>
);

const ReportOnePager = ({ report, record, onCopy, onCopyShareLink, hidePrivateActions }) => {
  const {
    business = "", trade = "", location = "", headline = "", summary = "",
    ranking = [], advertisers = [], complaints = [], lookalikes = [], openings = [],
  } = report || {};

  const clientRank = ranking.find((r) => r.is_client);
  const dateStr = record ? new Date(record.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : todayLabel();

  const maxReviews = Math.max(1, ...ranking.map((r) => parseCount(r.reviews)));
  const clientReviews = clientRank ? parseCount(clientRank.reviews) : 0;
  const proofGap = clientReviews > 0 ? maxReviews / clientReviews : 0;

  const leaderIdx = ranking.reduce((best, r, i) => {
    if (r.is_client) return best;
    return best === -1 || parseCount(r.reviews) > parseCount(ranking[best].reviews) ? i : best;
  }, -1);
  const showLeader = leaderIdx >= 0 && parseCount(ranking[leaderIdx].reviews) >= clientReviews;

  let sec = 0;
  const nextNum = () => String(++sec).padStart(2, "0");

  return (
    <div className="mr-report" data-testid="mr-report">
      {/* ── Hero ── */}
      <header className="rp-hero" data-testid="report-hero">
        <div className="rp-hero-glow" aria-hidden="true" />
        <div className="rp-doc-tag">Market Domination Report</div>
        <div className="rp-prepared">Prepared for <span className="rp-prepared-name">{business || "—"}</span></div>
        <h1 className="rp-headline" data-testid="report-hero-headline">{headline}</h1>
        {summary && <p className="rp-summary">{summary}</p>}
        <div className="rp-meta">
          {location && <span className="rp-pill">{location}</span>}
          {trade && <span className="rp-pill">{trade}</span>}
          <span className="rp-pill">{dateStr}</span>
          <span className="rp-pill skye">Skye AI</span>
        </div>

        {(clientRank || ranking.length > 0 || openings.length > 0) && (
          <div className="rp-statband" data-testid="report-statband">
            {clientRank?.rating && (
              <div className="rp-stat">
                <div className="rp-stat-num pink">{clientRank.rating}<span className="rp-stat-unit">★</span></div>
                <div className="rp-stat-lbl">Your rating</div>
              </div>
            )}
            {proofGap >= 1.5 && (
              <div className="rp-stat">
                <div className="rp-stat-num cyan">{proofGap >= 10 ? Math.round(proofGap) : proofGap.toFixed(1)}<span className="rp-stat-unit">×</span></div>
                <div className="rp-stat-lbl">The proof gap — their reviews vs yours</div>
              </div>
            )}
            {ranking.length > 1 && (
              <div className="rp-stat">
                <div className="rp-stat-num">{ranking.filter((r) => !r.is_client).length}</div>
                <div className="rp-stat-lbl">Rivals tracked in your market</div>
              </div>
            )}
            {openings.length > 0 && (
              <div className="rp-stat">
                <div className="rp-stat-num purple">{Math.min(openings.length, 3)}</div>
                <div className="rp-stat-lbl">Openings we attack first</div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* ── Ranking Ladder ── */}
      {ranking.length > 0 && (
        <section className="rp-section" style={{ animationDelay: "80ms" }}>
          <SectionHead num={nextNum()} title="Where you stand today" sub="Bar length = share of review proof in your market. Longer bar, louder voice." />
          <div className="lad" data-testid="report-ladder">
            {ranking.map((row, i) => {
              const rv = parseCount(row.reviews);
              const pct = Math.max(5, Math.round((rv / maxReviews) * 100));
              const isLeader = showLeader && i === leaderIdx;
              return (
                <div key={i} className={`lad-row ${row.is_client ? "is-client" : ""} ${isLeader ? "is-leader" : ""}`} style={{ animationDelay: `${120 + i * 70}ms` }}>
                  <div className="lad-bar" style={{ width: `${pct}%` }} />
                  <div className="lad-pos">{String(row.pos || i + 1).padStart(2, "0")}</div>
                  <div className="lad-main">
                    <div className="lad-name">
                      {row.name}
                      {row.is_client && <span className="lad-you">You</span>}
                      {isLeader && <span className="lad-top" data-testid="ladder-leader-tag">Loudest voice</span>}
                    </div>
                    {row.note && <div className="lad-note">{row.note}</div>}
                  </div>
                  <div className="lad-stats">
                    {row.rating && <span className="lad-rating">{row.rating}<i>★</i></span>}
                    {row.reviews && <span className="lad-reviews">{row.reviews} reviews</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {clientRank && (
            <div className="lad-caption">
              {Number(clientRank.rating || 0) >= 4.8
                ? "Best rating, smallest voice. That's the exact gap we close."
                : "This is where our booked-estimate engine plugs in."}
            </div>
          )}
          {!clientRank && (
            <div className="lad-caption warn">
              You don&apos;t show up in the local search rankings yet — that&apos;s finding #1.
            </div>
          )}
        </section>
      )}

      {/* ── Advertisers ── */}
      {advertisers.length > 0 && (
        <section className="rp-section" style={{ animationDelay: "120ms" }}>
          <SectionHead num={nextNum()} title="Who is out-marketing you right now" />
          <div className="adv-grid" data-testid="report-advertisers">
            {advertisers.map((a, i) => (
              <div key={i} className="adv-card" style={{ animationDelay: `${160 + i * 90}ms` }}>
                <div className="adv-head">
                  <div className="adv-name">{a.name}</div>
                  {a.badge && <span className="adv-badge" data-testid={`adv-badge-${i}`}>{a.badge}</span>}
                </div>
                <div className="adv-block">
                  <div className="adv-lbl">Their play</div>
                  <div className="adv-body">{a.angle}</div>
                </div>
                <div className="adv-block gap">
                  <div className="adv-lbl pink">The gap we attack</div>
                  <div className="adv-body">{a.weakness}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Complaints ── */}
      {complaints.length > 0 && (
        <section className="rp-section" style={{ animationDelay: "140ms" }}>
          <SectionHead num={nextNum()} title="What their customers complain about" sub="Local market complaints — not yours. Every one is a promise you get to make." />
          <div className="cmp-grid" data-testid="report-complaints">
            {complaints.map((c, i) => (
              <div key={i} className="cmp-card" style={{ animationDelay: `${160 + i * 90}ms` }}>
                <div className="cmp-theme">{c.theme}</div>
                {(c.seen || c.source) && (
                  <div className="cmp-chips">
                    {c.seen && <span className="cmp-chip">{c.seen}</span>}
                    {c.source && <span className="cmp-src" data-testid={`cmp-source-${i}`}>{c.source}</span>}
                  </div>
                )}
                <div className="cmp-quote">
                  <span className="cmp-qmark">&ldquo;</span>
                  {c.evidence}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Lookalikes ── */}
      {lookalikes.length > 0 && (
        <section className="rp-section" style={{ animationDelay: "160ms" }}>
          <SectionHead num={nextNum()} title="The name collision" sub="Businesses trading on your name that can bleed your search traffic." />
          <div className="lk-grid" data-testid="report-lookalikes">
            {lookalikes.map((l, i) => {
              const unconfirmed = (l.status || "").toLowerCase().includes("unconfirmed");
              return (
                <div key={i} className={`lk-card ${unconfirmed ? "warn" : ""}`} style={{ animationDelay: `${180 + i * 90}ms` }}>
                  <div className="lk-top">
                    <div className="lk-name">{l.name}</div>
                    <span className={`lk-chip ${unconfirmed ? "warn" : "ok"}`}>{l.status || "unknown"}</span>
                  </div>
                  <div className="lk-note">{l.note}</div>
                  {unconfirmed && (
                    <div className="lk-hint">If this is your other brand, we consolidate it. If not, we defend your name in search.</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Openings ── */}
      {openings.length > 0 && (
        <section className="rp-section" style={{ animationDelay: "180ms" }}>
          <SectionHead num={nextNum()} title="The openings we are attacking" sub="Each one traces to a competitor weakness or a complaint above. This is the battle plan." />
          <div className="op-grid" data-testid="report-openings">
            {openings.slice(0, 3).map((o, i) => (
              <div key={i} className="op-card" style={{ animationDelay: `${220 + i * 110}ms` }}>
                <div className="op-watermark">{String(i + 1).padStart(2, "0")}</div>
                <div className="op-num">Opening {String(i + 1).padStart(2, "0")}</div>
                <div className="op-title">{o.title}</div>
                <div className="op-why">{o.why}</div>
                <div className="op-move">
                  <div className="op-move-lbl">Our move</div>
                  <div className="op-move-body">{o.move}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Footer ── */}
      <footer className="rp-footer">
        <div className="rp-footer-note">
          Built from public data on {dateStr} — Google Business Profiles, customer reviews, and public search.
          <span className="rp-footer-strong"> Your campaign is built off these openings.</span>
        </div>
        <div className="rp-footer-actions">
          {!hidePrivateActions && (
            <button className="rp-btn ghost" onClick={onCopy} data-testid="mr-copy-text">Copy text</button>
          )}
          {!hidePrivateActions && onCopyShareLink && (
            <button className="rp-btn share" onClick={onCopyShareLink} data-testid="mr-copy-share">Copy share link</button>
          )}
        </div>
      </footer>
    </div>
  );
};

// Flatten the report to plaintext for the Copy button
const plainTextFromReport = (r) => {
  const lines = [];
  if (r.business) lines.push(`PREPARED FOR ${r.business.toUpperCase()}`);
  if (r.headline) lines.push(r.headline);
  if (r.summary) lines.push("", r.summary);
  if (r.location || r.trade) lines.push(`\n${[r.location, r.trade].filter(Boolean).join(" • ")}`);
  const push = (title, arr, fmt) => {
    if (!arr || arr.length === 0) return;
    lines.push(`\n\n${title.toUpperCase()}`);
    arr.forEach((x, i) => lines.push(fmt(x, i)));
  };
  push("Where you stand today", r.ranking || [], (x) =>
    `  #${x.pos || "?"} ${x.name} — ★${x.rating || "?"} (${x.reviews || "?"} reviews)${x.is_client ? "  ← YOU" : ""}${x.note ? " · " + x.note : ""}`);
  push("Who is out-marketing you", r.advertisers || [], (x) =>
    `  • ${x.name}${x.badge ? ` [${x.badge}]` : ""} — ${x.angle}  (gap: ${x.weakness})`);
  push("Customer complaints in the market", r.complaints || [], (x) =>
    `  • ${x.theme}: "${x.evidence}" (${[x.seen, x.source].filter(Boolean).join(" · ")})`);
  push("Name collisions", r.lookalikes || [], (x) =>
    `  • ${x.name} [${x.status}] — ${x.note || ""}`);
  push("Openings we are attacking", r.openings || [], (x, i) =>
    `  0${i + 1}. ${x.title}\n     Why: ${x.why}\n     Our move: ${x.move}`);
  return lines.join("\n");
};

// ─── Styles ────────────────────────────────────────────────────────────────
const styles = `
.mr-shell {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #050507;
  color: #fff;
  font-family: 'Manrope', 'Inter', -apple-system, system-ui, sans-serif;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ── Input pane ── */
.mr-input { padding: 22px 28px 20px; border-bottom: 1px solid rgba(255,255,255,0.05); background: linear-gradient(180deg, rgba(220,50,140,0.04), rgba(157,50,243,0.02)); }
.mr-input-inner { max-width: 960px; margin: 0 auto; }
.mr-input-title { font-family: 'Space Grotesk', 'Manrope', sans-serif; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; margin-bottom: 4px; background: linear-gradient(135deg, #f472b6, #22d3ee); -webkit-background-clip: text; background-clip: text; color: transparent; }
.mr-input-sub { font-size: 12px; color: rgba(255,255,255,0.5); margin-bottom: 16px; }
.mr-input-row { display: flex; gap: 10px; margin-bottom: 10px; }
.mr-url { flex: 1; padding: 12px 16px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.35); color: #fff; font-size: 14px; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; }
.mr-url:focus { outline: none; border-color: rgba(244,114,182,0.55); box-shadow: 0 0 0 3px rgba(244,114,182,0.12); }
.mr-generate { padding: 12px 22px; border-radius: 10px; border: 0; background: linear-gradient(135deg, #dc328c, #9d32f3); color: #fff; font-size: 13px; font-weight: 800; letter-spacing: 0.02em; cursor: pointer; font-family: 'Manrope', sans-serif; transition: transform .18s, box-shadow .18s; box-shadow: 0 6px 22px -6px rgba(220,50,140,0.6); }
.mr-generate:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 28px -6px rgba(220,50,140,0.75); }
.mr-generate:disabled { opacity: 0.6; cursor: wait; }
.mr-advanced-toggle { background: none; border: 0; color: rgba(255,255,255,0.4); font-size: 11px; font-family: 'IBM Plex Mono', monospace; cursor: pointer; padding: 6px 0; letter-spacing: 0.02em; }
.mr-advanced-toggle:hover { color: rgba(244,114,182,0.9); }
.mr-adv-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 10px 0; }
.mr-adv-input { padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); background: rgba(0,0,0,0.3); color: #fff; font-size: 12px; }
.mr-status { display: flex; align-items: center; gap: 8px; padding: 8px 0; font-size: 11px; color: rgba(255,255,255,0.55); font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.01em; }
.mr-status.loading { color: #f472b6; }
.mr-status.err { padding: 10px 14px; border-radius: 8px; border: 1px solid rgba(248,113,113,0.35); background: rgba(248,113,113,0.08); color: #fca5a5; font-size: 12.5px; font-family: 'Manrope', 'Inter', sans-serif; letter-spacing: 0; line-height: 1.45; }
.mr-status.err::before { content: "⚠"; font-size: 14px; color: #f87171; }
.mr-spinner { width: 12px; height: 12px; border: 2px solid rgba(244,114,182,0.25); border-top-color: #f472b6; border-radius: 50%; animation: mrspin 0.8s linear infinite; }
.mr-spinner.big { width: 32px; height: 32px; border-width: 3px; }
@keyframes mrspin { to { transform: rotate(360deg); } }

.mr-history { margin-top: 12px; padding-top: 12px; border-top: 1px dashed rgba(255,255,255,0.08); }
.mr-history-label { font-size: 10px; font-family: 'IBM Plex Mono', monospace; text-transform: uppercase; color: rgba(255,255,255,0.35); letter-spacing: 0.15em; margin-bottom: 6px; }
.mr-history-list { display: flex; flex-wrap: wrap; gap: 6px; }
.mr-history-chip-wrap { display: inline-flex; align-items: stretch; border-radius: 999px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.02); overflow: hidden; transition: border-color .15s; }
.mr-history-chip-wrap:hover { border-color: rgba(244,114,182,0.5); }
.mr-history-chip { display: inline-flex; align-items: center; gap: 8px; padding: 5px 10px 5px 12px; border: 0; background: transparent; color: rgba(255,255,255,0.7); font-size: 11px; cursor: pointer; transition: color .15s; font-family: 'Inter', sans-serif; }
.mr-history-chip:hover { color: #fff; }
.mr-history-del { display: inline-flex; align-items: center; justify-content: center; width: 22px; padding: 0 4px; border: 0; border-left: 1px solid rgba(255,255,255,0.06); background: transparent; color: rgba(255,255,255,0.35); font-size: 15px; line-height: 1; cursor: pointer; transition: color .15s, background .15s; }
.mr-history-del:hover { color: #fff; background: rgba(244,114,182,0.18); }
.mr-chip-views { display: inline-flex; align-items: center; gap: 3px; font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 700; color: #22d3ee; }

.mr-report-pane { flex: 1; padding: 34px 20px 80px; display: flex; justify-content: center; }
.mr-report-col { width: 100%; max-width: 980px; display: flex; flex-direction: column; gap: 14px; }
.mr-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 12px; padding: 40px; }
.mr-empty-icon { font-size: 42px; color: rgba(244,114,182,0.3); }
.mr-empty-title { font-family: 'Space Grotesk', sans-serif; font-size: 18px; font-weight: 700; color: rgba(255,255,255,0.75); text-align: center; }
.mr-empty-sub { font-size: 12px; color: rgba(255,255,255,0.4); text-align: center; max-width: 400px; line-height: 1.6; }

/* ── View analytics strip ── */
.va-strip { border: 1px solid rgba(34,211,238,0.22); border-radius: 12px; background: linear-gradient(90deg, rgba(34,211,238,0.06), rgba(34,211,238,0.01)); padding: 10px 16px; font-family: 'IBM Plex Mono', monospace; font-size: 11px; color: rgba(255,255,255,0.6); }
.va-strip.empty { border-color: rgba(255,255,255,0.08); background: rgba(255,255,255,0.015); display: flex; align-items: center; gap: 8px; }
.va-none { color: rgba(255,255,255,0.42); }
.va-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.va-eye { color: rgba(255,255,255,0.35); display: inline-flex; }
.va-eye.live { color: #22d3ee; }
.va-stat b { color: #fff; font-weight: 700; }
.va-dot { color: rgba(255,255,255,0.25); }
.va-toggle { margin-left: auto; background: none; border: 1px solid rgba(34,211,238,0.35); color: #22d3ee; font-family: inherit; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 4px 12px; border-radius: 999px; cursor: pointer; transition: background .15s, color .15s; }
.va-toggle:hover { background: rgba(34,211,238,0.12); color: #fff; }
.va-sessions { margin-top: 10px; border-top: 1px dashed rgba(255,255,255,0.1); padding-top: 8px; display: flex; flex-direction: column; gap: 4px; max-height: 220px; overflow-y: auto; }
.va-session { display: flex; justify-content: space-between; gap: 12px; padding: 5px 4px; font-size: 10.5px; }
.va-s-when { color: #fff; font-weight: 600; }
.va-s-detail { color: rgba(255,255,255,0.45); }

/* ══════════════ THE REPORT ══════════════ */
.mr-report { width: 100%; max-width: 980px; position: relative; padding: 12px 8px 8px; }

@keyframes rpFadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }

/* ── Hero ── */
.rp-hero { position: relative; padding: 56px 0 44px; margin-bottom: 26px; animation: rpFadeUp .6s ease-out both; }
.rp-hero-glow { position: absolute; inset: -80px -200px auto; height: 480px; pointer-events: none; background: radial-gradient(ellipse 60% 55% at 18% 8%, rgba(157,50,243,0.16), transparent 60%), radial-gradient(ellipse 45% 45% at 78% 0%, rgba(220,50,140,0.12), transparent 55%); z-index: 0; }
.rp-hero > *:not(.rp-hero-glow) { position: relative; z-index: 1; }
.rp-doc-tag { display: inline-block; font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.28em; text-transform: uppercase; color: rgba(255,255,255,0.45); border: 1px solid rgba(255,255,255,0.14); padding: 6px 14px; border-radius: 3px; margin-bottom: 26px; }
.rp-prepared { font-family: 'IBM Plex Mono', monospace; font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(255,255,255,0.5); margin-bottom: 16px; }
.rp-prepared-name { background: linear-gradient(90deg, #f472b6, #22d3ee); -webkit-background-clip: text; background-clip: text; color: transparent; font-weight: 700; }
.rp-headline { font-family: 'Space Grotesk', sans-serif; font-size: clamp(32px, 5vw, 54px); font-weight: 700; letter-spacing: -0.03em; line-height: 1.05; margin: 0 0 22px; color: #fff; max-width: 860px; }
.rp-summary { font-size: clamp(15px, 1.6vw, 17px); line-height: 1.65; color: rgba(255,255,255,0.72); margin: 0 0 26px; max-width: 700px; }
.rp-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 36px; }
.rp-pill { padding: 5px 14px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.12); font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(255,255,255,0.55); }
.rp-pill.skye { border-color: rgba(220,50,140,0.5); color: #f472b6; background: rgba(220,50,140,0.07); }

.rp-statband { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 1px; background: rgba(255,255,255,0.09); border: 1px solid rgba(255,255,255,0.09); border-radius: 14px; overflow: hidden; }
.rp-stat { background: #0b0b10; padding: 22px 22px 18px; }
.rp-stat-num { font-family: 'IBM Plex Mono', monospace; font-size: clamp(30px, 3.4vw, 42px); font-weight: 700; letter-spacing: -0.04em; line-height: 1; color: #fff; margin-bottom: 8px; }
.rp-stat-num.pink { color: #f472b6; }
.rp-stat-num.cyan { color: #22d3ee; }
.rp-stat-num.purple { color: #c084fc; }
.rp-stat-unit { font-size: 0.55em; opacity: 0.75; margin-left: 2px; }
.rp-stat-lbl { font-size: 11px; line-height: 1.45; color: rgba(255,255,255,0.45); font-weight: 500; }

/* ── Section heads ── */
.rp-section { margin-bottom: 64px; animation: rpFadeUp .6s ease-out both; }
.sec-head { margin-bottom: 26px; }
.sec-topline { display: flex; align-items: center; gap: 14px; margin-bottom: 12px; }
.sec-num { font-family: 'IBM Plex Mono', monospace; font-size: 12px; font-weight: 700; color: #dc328c; letter-spacing: 0.1em; }
.sec-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(220,50,140,0.45), rgba(255,255,255,0.05) 55%, transparent); }
.sec-title { font-family: 'Space Grotesk', sans-serif; font-size: clamp(22px, 2.6vw, 28px); font-weight: 700; letter-spacing: -0.025em; margin: 0; color: #fff; }
.sec-sub { font-size: 13px; color: rgba(255,255,255,0.42); margin-top: 8px; max-width: 620px; line-height: 1.5; }

/* ── Ranking ladder ── */
.lad { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.lad-row { position: relative; display: flex; align-items: center; gap: 18px; min-height: 62px; padding: 12px 20px; border-radius: 10px; background: #0b0b10; border: 1px solid rgba(255,255,255,0.06); overflow: hidden; animation: rpFadeUp .5s ease-out both; }
.lad-bar { position: absolute; left: 0; top: 0; bottom: 0; background: linear-gradient(90deg, rgba(255,255,255,0.055), rgba(255,255,255,0.015)); border-right: 1px solid rgba(255,255,255,0.06); pointer-events: none; transition: width .6s ease-out; }
.lad-row.is-client { border: 2px solid #dc328c; background: #14080f; animation: rpFadeUp .5s ease-out both, ladPulse 2.6s ease-in-out infinite; }
.lad-row.is-client .lad-bar { background: linear-gradient(90deg, rgba(220,50,140,0.22), rgba(220,50,140,0.05)); border-right-color: rgba(220,50,140,0.45); }
@keyframes ladPulse { 0%, 100% { box-shadow: 0 0 22px -8px rgba(220,50,140,0.55); } 50% { box-shadow: 0 0 34px -6px rgba(220,50,140,0.8); } }
.lad-pos { position: relative; font-family: 'IBM Plex Mono', monospace; font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.32); min-width: 28px; }
.lad-row.is-client .lad-pos { color: #f472b6; }
.lad-main { position: relative; flex: 1; min-width: 0; }
.lad-name { font-family: 'Space Grotesk', sans-serif; font-size: 15px; font-weight: 700; letter-spacing: -0.01em; color: #fff; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.lad-row.is-client .lad-name { color: #f9a8d4; }
.lad-you { font-family: 'IBM Plex Mono', monospace; font-size: 9px; padding: 3px 10px; border-radius: 999px; background: #dc328c; color: #fff; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; }
.lad-top { font-family: 'IBM Plex Mono', monospace; font-size: 9px; padding: 3px 10px; border-radius: 999px; background: rgba(251,191,36,0.14); border: 1px solid rgba(251,191,36,0.35); color: #fbbf24; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; }
.lad-row.is-leader { border-color: rgba(251,191,36,0.3); }
.lad-row.is-leader .lad-bar { background: linear-gradient(90deg, rgba(251,191,36,0.13), rgba(251,191,36,0.03)); border-right-color: rgba(251,191,36,0.4); }
.lad-note { font-size: 11.5px; color: rgba(255,255,255,0.45); margin-top: 3px; line-height: 1.4; }
.lad-stats { position: relative; display: flex; flex-direction: column; align-items: flex-end; gap: 2px; font-family: 'IBM Plex Mono', monospace; white-space: nowrap; }
.lad-rating { font-size: 16px; font-weight: 700; color: #fff; letter-spacing: -0.02em; }
.lad-rating i { font-style: normal; font-size: 11px; color: #fbbf24; margin-left: 2px; }
.lad-row.is-client .lad-rating { color: #f472b6; }
.lad-reviews { font-size: 10px; color: rgba(255,255,255,0.4); letter-spacing: 0.04em; }
.lad-caption { font-size: 13.5px; color: rgba(255,255,255,0.65); font-style: italic; padding-left: 4px; border-left: 3px solid rgba(220,50,140,0.6); padding: 4px 0 4px 14px; }
.lad-caption.warn { color: #fca5a5; border-left-color: rgba(252,165,165,0.6); }

/* ── Advertisers ── */
.adv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; }
.adv-card { background: #0b0b10; border: 1px solid rgba(255,255,255,0.06); border-left: 3px solid #9d32f3; border-radius: 10px; padding: 24px 24px 20px; animation: rpFadeUp .5s ease-out both; }
.adv-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
.adv-name { font-family: 'Space Grotesk', sans-serif; font-size: 17px; font-weight: 700; letter-spacing: -0.015em; color: #fff; }
.adv-badge { font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; padding: 5px 13px; border-radius: 999px; background: linear-gradient(135deg, #f59e0b, #fbbf24); color: #1a1205; line-height: 1.5; box-shadow: 0 4px 14px -4px rgba(245,158,11,0.55); }
.adv-block { margin-bottom: 14px; }
.adv-block:last-child { margin-bottom: 0; }
.adv-lbl { font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(255,255,255,0.38); margin-bottom: 6px; }
.adv-lbl.pink { color: #f472b6; }
.adv-body { font-size: 13px; line-height: 1.6; color: rgba(255,255,255,0.72); }
.adv-block.gap { padding-top: 14px; border-top: 1px dashed rgba(255,255,255,0.1); }
.adv-block.gap .adv-body { color: rgba(255,255,255,0.85); }

/* ── Complaints ── */
.cmp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; }
.cmp-card { background: #0b0b10; border: 1px solid rgba(255,255,255,0.06); border-left: 3px solid rgba(248,113,113,0.65); border-radius: 10px; padding: 22px 24px; animation: rpFadeUp .5s ease-out both; }
.cmp-theme { font-family: 'Space Grotesk', sans-serif; font-size: 16px; font-weight: 700; letter-spacing: -0.015em; color: #fff; margin-bottom: 10px; }
.cmp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.cmp-chip { display: inline-block; font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; padding: 5px 12px; border-radius: 8px; background: rgba(248,113,113,0.12); color: #fca5a5; line-height: 1.6; max-width: 100%; }
.cmp-src { display: inline-block; font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; padding: 5px 12px; border-radius: 8px; border: 1px solid rgba(34,211,238,0.3); background: rgba(34,211,238,0.07); color: #22d3ee; line-height: 1.6; max-width: 100%; }
.cmp-quote { position: relative; font-size: 14px; line-height: 1.65; color: rgba(255,255,255,0.78); font-style: italic; padding-left: 26px; }
.cmp-qmark { position: absolute; left: 0; top: -6px; font-family: 'Space Grotesk', serif; font-size: 34px; line-height: 1; color: rgba(248,113,113,0.55); font-style: normal; }

/* ── Lookalikes ── */
.lk-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; }
.lk-card { background: #0b0b10; border: 1px solid rgba(255,255,255,0.06); border-radius: 10px; padding: 22px 24px; animation: rpFadeUp .5s ease-out both; }
.lk-card.warn { border-color: rgba(251,191,36,0.3); background: #100d06; }
.lk-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.lk-name { font-family: 'Space Grotesk', sans-serif; font-size: 15px; font-weight: 700; color: #fff; }
.lk-chip { font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; padding: 4px 10px; border-radius: 8px; line-height: 1.6; flex-shrink: 1; background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.55); }
.lk-chip.ok { background: rgba(74,222,128,0.1); color: #4ADE80; }
.lk-chip.warn { background: rgba(251,191,36,0.13); color: #fbbf24; }
.lk-note { font-size: 13px; line-height: 1.6; color: rgba(255,255,255,0.7); }
.lk-hint { margin-top: 12px; padding-top: 12px; border-top: 1px dashed rgba(255,255,255,0.1); font-size: 12px; color: rgba(255,255,255,0.45); font-style: italic; line-height: 1.5; }

/* ── Openings ── */
.op-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: 14px; }
.op-card { position: relative; background: linear-gradient(180deg, #0e0a14, #0a0a10); border: 1px solid rgba(157,50,243,0.28); border-radius: 12px; padding: 30px 26px 26px; overflow: hidden; animation: rpFadeUp .5s ease-out both; transition: border-color .2s; }
.op-card:hover { border-color: rgba(220,50,140,0.5); }
.op-watermark { position: absolute; top: -28px; right: -8px; font-family: 'IBM Plex Mono', monospace; font-size: 8.5rem; font-weight: 700; color: rgba(255,255,255,0.045); line-height: 1; pointer-events: none; user-select: none; }
.op-num { font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase; color: #c084fc; margin-bottom: 14px; }
.op-title { font-family: 'Space Grotesk', sans-serif; font-size: 19px; font-weight: 700; letter-spacing: -0.02em; line-height: 1.25; color: #fff; margin-bottom: 14px; position: relative; }
.op-why { font-size: 13px; line-height: 1.65; color: rgba(255,255,255,0.62); margin-bottom: 20px; position: relative; }
.op-move { border-top: 1px solid rgba(34,211,238,0.25); padding-top: 16px; position: relative; }
.op-move-lbl { font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase; color: #22d3ee; margin-bottom: 8px; }
.op-move-body { font-family: 'IBM Plex Mono', monospace; font-size: 12px; line-height: 1.7; color: rgba(255,255,255,0.82); }

/* ── Gaps ── */
.gap-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.gap-item { display: flex; align-items: flex-start; gap: 14px; padding: 14px 18px; border-radius: 10px; background: #0b0b10; border: 1px solid rgba(255,255,255,0.06); font-size: 13.5px; line-height: 1.55; color: rgba(255,255,255,0.78); }
.gap-mark { flex-shrink: 0; width: 14px; height: 14px; margin-top: 3px; border: 1.5px solid rgba(244,114,182,0.6); border-radius: 3px; }

/* ── Footer ── */
.rp-footer { margin-top: 10px; padding-top: 26px; border-top: 1px solid rgba(255,255,255,0.08); display: flex; justify-content: space-between; align-items: center; gap: 18px; flex-wrap: wrap; }
.rp-footer-note { font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; line-height: 1.7; color: rgba(255,255,255,0.35); flex: 1; min-width: 240px; max-width: 520px; }
.rp-footer-strong { color: rgba(255,255,255,0.7); font-weight: 700; }
.rp-footer-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.rp-btn { padding: 10px 20px; border-radius: 999px; border: 1px solid rgba(244,114,182,0.45); background: linear-gradient(135deg, rgba(220,50,140,0.16), rgba(157,50,243,0.1)); color: #f472b6; font-size: 11.5px; font-weight: 800; cursor: pointer; letter-spacing: 0.04em; font-family: 'Manrope', sans-serif; transition: background .15s, color .15s, border-color .15s; }
.rp-btn:hover { background: linear-gradient(135deg, rgba(220,50,140,0.3), rgba(157,50,243,0.18)); color: #fff; }
.rp-btn.ghost { background: transparent; border-color: rgba(255,255,255,0.15); color: rgba(255,255,255,0.6); }
.rp-btn.ghost:hover { border-color: rgba(255,255,255,0.35); color: #fff; }
.rp-btn.share { background: rgba(34,211,238,0.1); border-color: rgba(34,211,238,0.45); color: #22d3ee; }
.rp-btn.share:hover { background: rgba(34,211,238,0.22); color: #fff; border-color: rgba(34,211,238,0.7); }

/* ── Responsive ── */
@media (max-width: 760px) {
  .mr-input { padding: 16px 14px; }
  .mr-input-row { flex-direction: column; }
  .mr-adv-row { grid-template-columns: 1fr; }
  .mr-report-pane { padding: 20px 12px 60px; }
  .rp-hero { padding: 30px 0 28px; }
  .rp-hero-glow { inset: -40px 0 auto; }
  .rp-statband { grid-template-columns: repeat(2, 1fr); }
  .adv-grid, .cmp-grid, .lk-grid, .op-grid { grid-template-columns: 1fr; }
  .lad-row { padding: 12px 14px; gap: 12px; }
  .lad-stats { align-items: flex-end; }
  .rp-section { margin-bottom: 44px; }
  .va-toggle { margin-left: 0; }
}
`;

export default MarketReport;
export { ReportOnePager, styles as reportStyles };
